import { useEffect, useRef } from 'react'

import { Link, useLocation, useNavigationType } from 'react-router-dom'
import usePostStatsContext from '@contexts/postStatsContext'
import { localAccessors } from '@utils/localStateAccess'
import useSessionState from '@hooks/useSessionState'
import { colors, size } from '@styles/theme'
import useFeed from '@hooks/useFeed'
import styled from '@emotion/styled'

import { NotificationLikeIcon, NotificationRepostIcon } from './NotificationIcons'
import BackTopbar, { TopbarClearfix } from '@elements/BackTopbar'
import ModalContainer from '@elements/ModalContainer'
import { PiArrowRightBold } from 'react-icons/pi'
import Post from '@components/feeds/post/Post'
import EndDot from '@elements/EndDot'

const ContentContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100dvw;
  height: 100dvh;

  overflow-y: scroll;
`

const UserLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  padding: ${size(2)} ${size(2.5)};
  margin: ${size(3.5)} ${size(1.5)} ${size(2.5)} ${size(1.5)};
  overflow: hidden;
  border-radius: 100px;
  
  text-decoration: none;
  font-weight: 600;
  color: white;
  white-space: nowrap;

  background-color: ${colors.statusColors.right_now('primary')};
`

const UserText = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`

const UserArrow = styled.div`
  flex-shrink: 0;
  padding-left: ${size(1)};
`

const backgroundStyles = {
  backgroundColor: 'black',
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100dvh',
  width: '100dvw',
  overflowY: 'scroll',
}

const NotificationsPostListModal = ({ open, setClosed, }) => {
  const location = useLocation()
  const firstRender = useRef(true)
  const navigationType = useNavigationType()
  const postStats = usePostStatsContext()
  // NOTE: I tried to get the scroll position working here, but it looks like the value of scrollTop on unmount is always zero because the height of the element turns to zero.
  //       I don't know how to fix this yet, and will return to it later
  // const [scrollEl, setScrollEl] = useState(null)
  // const setScrollTopToStorage = () => { sessionStorage.setItem('notifications-post-list-scrollTop', scrollEl?.scrollTop || 0) };
  // const getScrollTopFromStorage = () => { scrollEl.scrollTop = sessionStorage.getItem('notifications-post-list-scrollTop') || 0 };

  // sets the scrollTop to correct place on backnavigation
  // useEffect(() => {
  //   if (open && scrollEl) {
  //     console.log('entering', sessionStorage.getItem('notifications-post-list-scrollTop'))
  //     if (navigationType === 'POP') getScrollTopFromStorage()
  //     return () => {
  //       console.log('exiting', scrollEl.scrollTop)
  //       setScrollTopToStorage()
  //     }
  //   }    
  // }, [open, scrollEl])

  // Feed State/Utilities
  const [action, setAction] = useSessionState(null)
  const [user, setUser] = useSessionState(null)
  const feedAccessor = localAccessors.notificationsPostList()
  const nearbyFeedState = useFeed({ feedAccessor, postStats })
  const {
    posts,
    setPosts,
    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,
  } = nearbyFeedState

  useEffect(() => {
    if (open) {
      firstRender.current = true
    } else if (!open) {
      firstRender.current = false
      if (navigationType === 'POP') {
        setPosts([])
        setAction(null)
        setUser(null)
      }
    }
  }, [open])

  useEffect(() => {
    const { posts, action, user } = location?.state || {}
    if (firstRender.current && posts && action && user && navigationType === 'PUSH') {
      setPosts(location?.state?.posts)
      setAction(location?.state?.action)
      setUser(location?.state?.user)
      firstRender.current = false
    }
  }, [location?.state])

  const renderTitleName = () => (
    (action === 'repost' ? 'Reposted' : 'Liked') + ' Posts' 
  )

  const renderLikeText = () => (
    <>
      <NotificationLikeIcon
        style={{ flexShrink: 0 }}
        size={size(2.5)}
      />
      &nbsp;&nbsp;Liked
    </>
  )

  const renderRespostText = () => (
    <>
      <NotificationRepostIcon
        style={{ flexShrink: 0 }}
        size={size(2.5)}
      />
      &nbsp;&nbsp;Reposted
    </>
  )

  const renderUserDisplay = () => {
    if (!user?.displayName || !action) return;
    // WARNING/TODO: There's a bug here where the action is somehow getting set to the user on backnavigation after clicking this button?
    //               It causes the the button to render the like display (since that's the default) even when the action should be repost
    return (
      <UserLink to={`/profiles/${user?.uuid}`}>
        <UserText>
          {action === 'repost' ? renderRespostText() : renderLikeText()}
          &nbsp;by {user?.displayName}
        </UserText>
        <UserArrow>
          <PiArrowRightBold size={size(2.5)} />
        </UserArrow>
      </UserLink>
    )
  }

  const renderPosts = () => {
    return posts?.map((post, index) => {
      return (
        <Post
          post={post}
          onlyMainDisplay
          beforeLikeHook={setLikeToState}
          beforeUnlikeHook={setUnlikeToState}
          beforeRepostHook={setRepostToState}
          newReplyAfterCreate={newReplyAfterCreate}
          afterDelete={afterPostDelete}
          feedAccessor={feedAccessor}
          index={index}
          key={index}
        />
      )
    })
  }

  return (
    <ModalContainer
      backgroundStyles={backgroundStyles}
      renderCustomHeader={() => <BackTopbar title={renderTitleName()} />}
      open={open}
      setClosed={setClosed}>
      <ContentContainer /*ref={(node) => node && setScrollEl(node)} */>
        <TopbarClearfix style={{ height: size(6.75) }} />
        {renderUserDisplay()}
        {renderPosts()}
        {posts?.length > 0 && <EndDot />}
      </ContentContainer>
    </ModalContainer>
  )
}

export default NotificationsPostListModal
